// usePolling.ts
import { useEffect, useRef } from "react";

export function usePolling({
  isActive,
  tick,
  intervalMs = 3000,
}: {
  /** Called before each loop to decide if polling should keep running */
  isActive: () => boolean;
  /** Your async polling function: do fetch + apply results here */
  tick: (signal: AbortSignal) => Promise<void>;
  intervalMs?: number;
}) {
  const aliveRef = useRef(true);
  const runningRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    aliveRef.current = true;

    const scheduleNext = () => {
      if (!aliveRef.current) return;
      timerRef.current = setTimeout(runOnce, intervalMs);
    };

    const runOnce = async () => {
      if (!aliveRef.current || !isActive()) {
        runningRef.current = false;
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        await tick(ac.signal);
      } catch (err) {
        if ((err as any)?.name !== "AbortError") {
          console.error("poll tick failed", err);
        }
      } finally {
        if (aliveRef.current && isActive()) {
          scheduleNext();
        } else {
          runningRef.current = false;
        }
      }
    };

    if (isActive() && !runningRef.current) {
      runningRef.current = true;
      scheduleNext();
    }

    return () => {
      aliveRef.current = false;
      runningRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [isActive, tick, intervalMs]);
}
